import { useState, useCallback, useEffect, useRef } from "react";
import { edgeFunctionService } from "../../supabase/edgeFunctions/edgeFunctionService";

export interface UseEdgeFunctionResult<T = any> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  execute: (payload?: any) => Promise<void>;
  reset: () => void;
}

/**
 * Generic hook for calling Edge Functions
 * Simple two-parameter design: function name and input
 *
 * @param functionName - Name of the Edge Function to call
 * @param input - Input to pass to the function (auto-executes if provided)
 */
export const useEdgeFunction = <T = any>(
  functionName: string,
  input?: any
): UseEdgeFunctionResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use ref to store the latest input to prevent double calls
  const inputRef = useRef<string>();

  const execute = useCallback(
    async (payload: any = {}) => {
      setLoading(true);
      setError(null);

      try {
        const result = await edgeFunctionService.invoke<T>(
          functionName,
          payload
        );

        if (result.error) {
          setError(result.error);
          setData(null);
        } else {
          setData(result.data);
        }
      } catch (err) {
        const errorObj =
          err instanceof Error ? err : new Error("Unknown error occurred");
        setError(errorObj);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [functionName]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  // Auto-execute if input is provided and has changed
  useEffect(() => {
    if (input !== undefined) {
      const inputString = JSON.stringify(input);
      // Only execute if input has actually changed
      if (inputRef.current !== inputString) {
        inputRef.current = inputString;
        execute(input);
      }
    }
  }, [input, execute]);

  return {
    data,
    loading,
    error,
    execute,
    reset,
  };
};
